import { network } from '@utils'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from '../root.ts'
import { EnablebankingCashAccountType } from '@root/constants/index.ts'

export enum EnableBankingPSUType {
  PERSONAL = 'personal',
  BUSINESS = 'business'
}

export enum EnableBankingAuthorizationFailureVariant {
  DEFAULT = 'default',
  OP = 'op'
}

export enum EnableBankingAccountConnectionBlock {
  NON_EUR = 'non_eur'
}

export enum EnableBankingCountry {
  Austria = 'AT',
  Belgium = 'BE',
  CzechRepublic = 'CZ',
  Germany = 'DE',
  Denmark = 'DK',
  Estonia = 'EE',
  Spain = 'ES',
  Finland = 'FI',
  France = 'FR',
  Greece = 'GR',
  Hungary = 'HU',
  Ireland = 'IE',
  Iceland = 'IS',
  Italy = 'IT',
  Lithuania = 'LT',
  Latvia = 'LV',
  Malta = 'MT',
  Netherlands = 'NL',
  Norway = 'NO',
  Poland = 'PL',
  Portugal = 'PT',
  Sweden = 'SE',
  Slovenia = 'SI',
  Slovakia = 'SK'
}

export const getDefaultEnableBankingCountry = (country?: string | null): EnableBankingCountry => {
  const code = country?.toUpperCase()
  return Object.values(EnableBankingCountry).includes(code as EnableBankingCountry)
    ? (code as EnableBankingCountry)
    : EnableBankingCountry.Finland
}

interface InstitutionProps {
  slug: string
  country: string
  psuType: EnableBankingPSUType
}

export interface IInstitution {
  name: string
  type: string
  bic: string
  logo: string
  country: string
}

export const fetchInstitutions: QueryFunction<InstitutionProps, IInstitution[]> = async ({
  slug,
  country,
  psuType
}) => {
  const url = `/v1/integrations/banking/${slug}/aspsps/${country}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: { psu_type: psuType }
  })
  return data
}

interface IRequisitionProps {
  slug: string
  aspspCountry: string
  aspspName: string
  psuType: EnableBankingPSUType
  redirectUrl: string
  errorRedirectUrl: string
}

export interface IRequisition {
  id: string
  url: string
}

export const fetchRequisition: QueryFunction<IRequisitionProps, IRequisition> = async ({
  slug,
  aspspCountry,
  aspspName,
  psuType,
  redirectUrl,
  errorRedirectUrl
}) => {
  const url = `/v1/integrations/banking/${slug}/session/${aspspCountry}/${aspspName}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: {
      psu_type: psuType,
      redirect_url: redirectUrl,
      error_redirect_url: errorRedirectUrl
    }
  })
  return data
}

interface IIntegrationListProps {
  slug: string
}

export interface IBankAccount {
  id: number
  uid: string
  identifier: string
  account_id: number
  last_sync_at: string
  begin_sync_from: string
  enabled: boolean
  cash_account_type?: EnablebankingCashAccountType
  name?: string
  details?: string
  connection_block?: EnableBankingAccountConnectionBlock | null
}

export interface IBankIntegration {
  id: number
  name: string
  country: string
  logo: string
  bic: string
  accounts: IBankAccount[]
  access_granted_at: string
  access_granted_until: string
  max_historical_days: number
  type: EnableBankingPSUType
  enabled: boolean
  is_staled: boolean
}

export const fetchBankIntegrations: QueryFunction<
  IIntegrationListProps,
  PaginatedResponse<IBankIntegration>
> = async ({ slug }) => {
  const url = `/v1/integrations/banking/${slug}/integrations/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

interface IntegrationInstanceProps extends IIntegrationListProps {
  integrationId: number
}

export const fetchBankIntegration: QueryFunction<
  IntegrationInstanceProps,
  IBankIntegration
> = async ({ slug, integrationId }) => {
  const url = `/v1/integrations/banking/${slug}/integrations/${integrationId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

interface RequisitionStatusProps {
  slug: string
  reference: string
}

export interface IRequisitionStatus {
  status: 'CREATED' | 'PROCESSING' | 'DONE' | 'FAILED' | 'CANCELLED'
}

export const fetchRequisitionStatus: QueryFunction<
  RequisitionStatusProps,
  IRequisitionStatus
> = async ({ slug, reference }) => {
  const url = `/v1/integrations/nordigen/${slug}/requisition_status/${reference}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IBankAccountInstanceProps {
  slug: string
  bankIntegrationId: number
  bankAccountId: number
}

export const updateBankAccount: MutationFunction<IBankAccountInstanceProps, IBankAccount> = async (
  { slug, bankIntegrationId, bankAccountId },
  requestData
) => {
  const url = `/v1/integrations/banking/${slug}/integrations/${bankIntegrationId}/accounts/${bankAccountId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteBankAccount: MutationFunction<IBankAccountInstanceProps, void> = async ({
  slug,
  bankAccountId
}) => {
  const url = `/v1/integrations/nordigen/${slug}/bank_account/${bankAccountId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}

export interface IIntegrationProps {
  slug: string
  integrationId: number
}

export const updateBankIntegration: MutationFunction<IIntegrationProps, IBankIntegration> = async (
  { slug, integrationId },
  requestData
) => {
  const url = `/v1/integrations/banking/${slug}/integrations/${integrationId}/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteBankIntegration: MutationFunction<IIntegrationProps, void> = async ({
  slug,
  integrationId
}) => {
  const url = `/v1/integrations/banking/${slug}/integrations/${integrationId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE'
  })
}

export interface IBackgroundFetchProps {
  slug: string
}

export const triggerBankIntegrationBackgroundFetch: QueryFunction<
  IBackgroundFetchProps,
  void
> = async ({ slug }) => {
  const url = `/v1/integrations/banking/${slug}/trigger_background_fetch/`
  await network.httpClient.request({
    url,
    method: 'POST'
  })
}

interface IIntegrationRenewProps {
  slug: string
}

interface IIntegrationRenewInfo {
  renew_pending: boolean
  renew_pending_soon: boolean
}

export const fetchIntegrationRenewInfo: QueryFunction<
  IIntegrationRenewProps,
  IIntegrationRenewInfo
> = async ({ slug }) => {
  const url = `/v1/integrations/banking/${slug}/has_integrations_to_renew/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}
